import {
  ComponentProps,
  RefObject,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import {createPortal} from 'react-dom';
import {twMerge} from 'tailwind-merge';

import CloseIcon from '@/assets/icons/cross.svg?react';
import {useBodyScrollLock} from '@/shared/hooks/useBodyScrollLock';
import {delay} from '@/shared/lib/delay';

const APPEAR_ANIMATION_MS = 250;

export type BaseModalApiRef = {
  close: () => Promise<void>;
};

export type BaseModalProps = Pick<
  ComponentProps<'div'>,
  'className' | 'children'
> & {
  fullScreen?: boolean;
  rootClassName?: ComponentProps<'div'>['className'];
  backdropProps?: Omit<ComponentProps<'div'>, 'onClick'>;
  backdropRef?: RefObject<HTMLDivElement>;
  appearSmoothBackdropAnimation?: boolean;
  disappearSmoothBackdropAnimation?: boolean;
  appearFromBottom?: boolean;
  disappearToBottom?: boolean;
  onClose?: () => void;
  withCloseButton?: boolean;
  refApi?: RefObject<BaseModalApiRef | null>;
};

function BaseModal({
  children,
  className,
  rootClassName,
  backdropProps = {},
  onClose,
  fullScreen = false,
  appearSmoothBackdropAnimation = false,
  disappearSmoothBackdropAnimation = false,
  withCloseButton = false,
  appearFromBottom = false,
  disappearToBottom = false,
  refApi,
}: BaseModalProps) {
  const {className: backdropClassName, ...restBackdropProps} = backdropProps;
  const [appeared, setAppeared] = useState(false);
  const [disappearing, setDisappearing] = useState(false);
  const shouldAnimateBackdrop =
    appearSmoothBackdropAnimation || disappearSmoothBackdropAnimation;
  const [canCloseByBackdrop, setCanCloseByBackdrop] = useState(
    !appearSmoothBackdropAnimation
  );
  useBodyScrollLock();

  useImperativeHandle<BaseModalApiRef, BaseModalApiRef>(refApi, () => {
    return {
      async close() {
        setDisappearing(true);
        await delay(APPEAR_ANIMATION_MS);
      },
    };
  });

  useEffect(() => {
    setAppeared(true);
  }, []);

  useEffect(() => {
    const timerId = setTimeout(() => {
      setCanCloseByBackdrop(true);
    }, APPEAR_ANIMATION_MS);

    return () => {
      clearTimeout(timerId);
    };
  }, []);

  const handleOnClose = useCallback(() => {
    if (!disappearToBottom) {
      onClose?.();
      return;
    }

    if (disappearing) {
      return;
    }

    setDisappearing(true);
    setTimeout(() => {
      onClose?.();
    }, APPEAR_ANIMATION_MS);
  }, [onClose, disappearing, disappearToBottom]);

  const handleByBackdropClick = useCallback(() => {
    if (!canCloseByBackdrop) {
      return;
    }

    handleOnClose();
  }, [canCloseByBackdrop, handleOnClose]);

  return createPortal(
    <div
      className={twMerge(
        'fixed inset-0 z-50 flex items-center justify-center',
        rootClassName
      )}
    >
      <div
        {...restBackdropProps}
        role={onClose ? 'dialog' : undefined}
        className={twMerge(
          'absolute h-full w-full bg-black/50',
          shouldAnimateBackdrop && 'transition-opacity duration-250',
          shouldAnimateBackdrop && !appeared && 'opacity-0',
          shouldAnimateBackdrop && appeared && !disappearing && 'opacity-100',
          shouldAnimateBackdrop &&
            disappearing &&
            disappearSmoothBackdropAnimation &&
            'opacity-0',
          backdropClassName
        )}
        {...(onClose ? {onClick: handleByBackdropClick} : {})}
      />
      <div
        className={twMerge(
          'relative z-10 my-auto max-h-[86dvh] w-fit max-w-[94dvw] sm:max-h-[90dvh]',
          fullScreen && 'my-0 h-full max-h-none max-w-none sm:max-h-none',
          appearFromBottom && 'animate-slide-in-bottom',
          disappearing && 'animate-slide-out-bottom',
          className
        )}
      >
        {withCloseButton && (
          <button
            className="absolute top-5 right-4 z-51 text-gray-500 hover:text-gray-700 focus:outline-hidden"
            onClick={handleOnClose}
          >
            <CloseIcon className="h-6 w-6" />
          </button>
        )}
        {children}
      </div>
    </div>,
    document.body
  );
}

export default BaseModal;
